import { useMemo, useRef, type ReactNode } from 'react';
import {
  Button,
  ColumnManager,
  EmptyState,
  FilterSheet,
  FilterableTH,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import { Money } from '@/components/ui/Money';
import type { BillingRegionLabeller } from '@/lib/billing-region';
import { sumDecimalStrings } from '@/lib/decimal';
import type { TargetActualRow, TargetMetric } from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useMediaQuery, useRowWindow } from '@/lib/use-row-window';
import { useTableFilters } from '@/lib/use-table-filters';
import {
  renderDrilldownCell,
  toColumnSpec,
  type DrilldownCellContext,
  type DrilldownColumn,
} from './drilldown-columns-shared';

/**
 * The table inside the target tile drill-down (owner 2026-10-28).
 *
 * One row per document behind a tile figure, ALL of them loaded at once
 * (`GET /targets/actuals/rows` has no paging), so every filter is page-scoped
 * and genuinely covers the whole set.
 *
 * ⚠️ **FILTER STATE IS COMPONENT STATE.** This lives in a Modal, not on a page:
 * nothing here reads or writes the URL, and `remember: false` keeps the funnels
 * out of the account's filter memory too (that memory is keyed by pathname, so
 * it would bleed into whichever dashboard opened the modal). Closing the modal
 * unmounts this and the filters go with it.
 *
 * ⚠️ **THE COLUMN LAYOUT IS REMEMBERED, per metric** (`drilldown.<metric>`),
 * because which columns somebody wants to see is a standing preference while a
 * filter is a question about one figure.
 *
 * ⚠️ **THE FOOTER TOTAL IS OVER THE FILTERED ROWS** and sums `convertedAmount`
 * only — one currency (`currency` = `meta.measuredInCurrency`) by construction.
 * Excluded rows carry `convertedAmount: null` and are skipped, never counted as
 * zero. The document-currency `amount` column is never totalled: its rows are in
 * several currencies. With no filter the total equals the tile minus any rows
 * the viewer cannot open (those are not in `rows` at all).
 *
 * Up to 10,000 rows can arrive, so the table and the phone cards are WINDOWED
 * (`useRowWindow`) and only the one the viewport needs is mounted.
 */

export interface DrilldownTableProps {
  metric: TargetMetric;
  rows: TargetActualRow[];
  columns: DrilldownColumn[];
  /**
   * Built once by the modal from `meta.measuredInCurrency` and
   * `useBillingRegionLabels()`. Its identity may change every render (the
   * labeller is fresh each time); this table re-derives a stable copy.
   */
  ctx: DrilldownCellContext;
}

const CONVERTED_KEY = 'convertedAmount';

function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

function rowCountText(count: number): string {
  return count === 1 ? '1 row' : `${count.toLocaleString()} rows`;
}

/**
 * A labeller whose identity only moves when a NAME moves.
 *
 * `useBillingRegionLabels()` (called by the modal) returns a fresh function every render. Feeding that
 * straight into the column specs would hand `useTableFilters` new columns on
 * every render and refilter up to 10,000 rows each time. The names are resolved
 * once for the regions these rows actually hold, and the signature of those
 * names is what the memo keys on.
 */
function useStableRegionLabeller(
  rows: TargetActualRow[],
  labelFor: BillingRegionLabeller,
): BillingRegionLabeller {
  const regions = useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) if (row.billingRegion !== null) seen.add(row.billingRegion);
    return Array.from(seen).sort();
  }, [rows]);
  const pairs = regions.map(
    (region) => [region, labelFor(region as Parameters<BillingRegionLabeller>[0])] as const,
  );
  const signature = JSON.stringify(pairs);
  return useMemo(() => {
    const names = new Map<string, string>(JSON.parse(signature) as [string, string][]);
    return (region) => names.get(region) ?? labelFor(region);
    // `labelFor` is deliberately not a dependency: it is fresh every render and
    // the signature already carries every name it can answer for these rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}

export function DrilldownTable({ metric, rows, columns, ctx: given }: DrilldownTableProps) {
  const currency = given.measuredInCurrency;
  const billingRegionLabel = useStableRegionLabeller(rows, given.billingRegionLabel);
  const ctx = useMemo<DrilldownCellContext>(
    () => ({ measuredInCurrency: currency, billingRegionLabel }),
    [currency, billingRegionLabel],
  );

  const specs = useMemo(() => columns.map((column) => toColumnSpec(column, ctx)), [columns, ctx]);
  const filters = useTableFilters(rows, specs, { remember: false });
  const { visibleRows, headerFor, activeCount, clearAll } = filters;

  const columnByKey = useMemo(
    () => new Map(columns.map((column): [string, DrilldownColumn] => [column.key, column])),
    [columns],
  );
  const columnKeys = useMemo(() => columns.map((column) => column.key), [columns]);
  // The first declared column identifies the record; a table without it is
  // not a narrower view of the documents, so it cannot be hidden or moved.
  const lockedKeys = useMemo(() => columnKeys.slice(0, 1), [columnKeys]);
  const layout = useColumnLayout(`drilldown.${metric}`, columnKeys, lockedKeys);
  const { visibleKeys, frozenSideOf } = layout;

  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);

  const headers = new Map<string, FilterableTHProps>(
    columnKeys.map((key): [string, FilterableTHProps] => [key, headerFor(key)]),
  );
  const labelOf = (key: string) => headers.get(key)?.label ?? key;

  const total = useMemo(
    () => sumDecimalStrings(visibleRows.map((row) => (row.excluded ? null : row.convertedAmount))),
    [visibleRows],
  );
  const convertedVisible = visibleKeys.includes(CONVERTED_KEY);

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const rowSetKey = useMemo(() => visibleRows.map((row) => row.key).join(','), [visibleRows]);
  const {
    start: tableStart,
    end: tableEnd,
    padTop: tablePadTop,
    padBottom: tablePadBottom,
    topSpacerRef: tableSpacerRef,
  } = useRowWindow(visibleRows.length, isDesktop && visibleRows.length > 60, rowSetKey);
  const {
    start: cardStart,
    end: cardEnd,
    padTop: cardPadTop,
    padBottom: cardPadBottom,
    topSpacerRef: cardSpacerRef,
  } = useRowWindow(
    visibleRows.length,
    !isDesktop && visibleRows.length > 40,
    rowSetKey,
    240,
    6,
  );

  if (rows.length === 0) {
    return <EmptyState title="No records sit behind this figure." />;
  }

  const filteredEmpty = visibleRows.length === 0;
  const columnCount = Math.max(visibleKeys.length, 1);
  const emptyMatches = (
    <EmptyState
      title="No rows match these filters."
      action={
        <Button variant="secondary" size="sm" onClick={clearAll}>
          Clear filters
        </Button>
      }
    />
  );

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
      <div className="flex min-w-0 items-center gap-2 text-xs text-fg-muted">
        {activeCount > 0 ? (
          <>
            <span className="tabular-nums">
              {visibleRows.length.toLocaleString()} of {rowCountText(rows.length)}
            </span>
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear filters
            </Button>
          </>
        ) : (
          <span className="tabular-nums">{rowCountText(rows.length)}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!isDesktop && (
          <FilterSheet
            keys={visibleKeys}
            headerFor={headerFor}
            activeCount={activeCount}
            clearAll={clearAll}
          />
        )}
        {/* Withheld until the stored layout has arrived: a toggle before then
         * would persist a hidden set derived from the default and overwrite
         * the arrangement still in flight. */}
        {!layout.loading && (
          <ColumnManager layout={layout} labelOf={labelOf} lockedKeys={lockedKeys} />
        )}
      </div>
    </div>
  );

  const totalFigure = <Money amount={total} currency={currency} />;

  if (!isDesktop) {
    return (
      <div>
        {toolbar}
        {filteredEmpty ? (
          emptyMatches
        ) : (
          <>
            <ul className="divide-y divide-border rounded-md border border-border">
              <li
                ref={cardSpacerRef}
                aria-hidden="true"
                style={{ height: cardPadTop }}
              />
              {visibleRows.slice(cardStart, cardEnd).map((row) => (
                <li key={row.key} className="px-3 py-3">
                  <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1.5 text-sm">
                    {visibleKeys.map((key) => {
                      const column = columnByKey.get(key);
                      if (!column) return null;
                      return (
                        <div key={key} className="contents">
                          <dt className="truncate text-xs text-fg-muted">{column.label}</dt>
                          <dd className="min-w-0 break-words">
                            {renderDrilldownCell(column, row, ctx)}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </li>
              ))}
              {cardPadBottom > 0 && (
                <li aria-hidden="true" style={{ height: cardPadBottom }} />
              )}
            </ul>
            <div className="sticky bottom-0 mt-2 flex items-center justify-between gap-3 rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm font-medium">
              <span className="tabular-nums">Total · {rowCountText(visibleRows.length)}</span>
              {totalFigure}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {toolbar}
      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
        <Table>
          <THead>
            {/* One cell per visible key, in order: `useFrozenOffsets` indexes
             * this row's children by position. */}
            <TR ref={headRowRef}>
              {visibleKeys.map((key) => (
                <FilterableTH
                  key={key}
                  {...(headers.get(key) ?? { label: key })}
                  {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                />
              ))}
            </TR>
          </THead>
          <TBody>
            {filteredEmpty ? (
              <TR>
                <TD colSpan={columnCount}>{emptyMatches}</TD>
              </TR>
            ) : (
              <>
                <tr ref={tableSpacerRef} aria-hidden="true">
                  <td
                    colSpan={columnCount}
                    style={{ height: tablePadTop, padding: 0, border: 0 }}
                  />
                </tr>
                {visibleRows.slice(tableStart, tableEnd).map((row) => (
                  <TR key={row.key}>
                    {visibleKeys.map((key) => {
                      const column = columnByKey.get(key);
                      return (
                        <TD
                          key={key}
                          numeric={column?.numeric || column?.filter === 'range'}
                          {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                        >
                          {column?.minWidth ? (
                            <div style={{ minWidth: column.minWidth }}>{renderDrilldownCell(column, row, ctx)}</div>
                          ) : column ? (
                            renderDrilldownCell(column, row, ctx)
                          ) : null}
                        </TD>
                      );
                    })}
                  </TR>
                ))}
                {tablePadBottom > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={columnCount}
                      style={{ height: tablePadBottom, padding: 0, border: 0 }}
                    />
                  </tr>
                )}
              </>
            )}
          </TBody>
          {!filteredEmpty && (
            <tfoot>
              {/* Sticky to the bottom of the scroll area. Not frozen sideways:
               * a frozen cell's own background and stacking classes would race
               * the footer's, so the footer scrolls horizontally with the body. */}
              <tr>
                {visibleKeys.map((key, index) => {
                  const column = columnByKey.get(key);
                  const numeric = column?.numeric || column?.filter === 'range';
                  let content: ReactNode = null;
                  if (key === CONVERTED_KEY) {
                    content = totalFigure;
                  } else if (index === 0) {
                    content = (
                      <span className="tabular-nums whitespace-nowrap">
                        Total · {rowCountText(visibleRows.length)}
                        {!convertedVisible && <> · {totalFigure}</>}
                      </span>
                    );
                  }
                  return (
                    <TD
                      key={key}
                      numeric={numeric}
                      className="sticky bottom-0 z-[15] bg-surface-sunken font-medium shadow-[inset_0_1px_0_0_var(--hairline)]"
                    >
                      {content}
                    </TD>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrap>
    </div>
  );
}
